import type { AlarmSchedule } from '@/graphql/types';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSharedFormContext } from '../../../reactForm/utils';
import type { TimeWindow, TimeWindowOnly } from '../../utils';
import { isTimeWindowEqual } from '../../utils';
import type { LeqAlarm, MaxAlarm, ModalFormData } from '../types';

/**
 * Finds the corresponding alarm schedule for a given modal form data (lden schedule).
 * Here we are basically looking for an alarm schedule that matches
 * the start and end times of the Lden schedule.
 *
 * @param ldenSchedule
 * @param alarmSchedules
 */
function findCorrespondingAlarmSchedule(
    ldenSchedule: ModalFormData,
    alarmSchedules: Array<AlarmSchedule>
) {
    const foundAlarmSchedule = alarmSchedules.find((alarmSchedule) =>
        isTimeWindowEqual(alarmSchedule as TimeWindow, ldenSchedule)
    );
    return {
        lmaxLevel: foundAlarmSchedule?.maxAlarms?.[0]?.alarmLevel,
        leqLevel: foundAlarmSchedule?.leqAlarms?.[0]?.alarmLevel,
        leqInterval: foundAlarmSchedule?.leqAlarms?.[0]?.interval,
    };
}

/**
 * Handles the management of the `notificationConfig.alarmSchedules` field by providing
 * utilities to save, delete and update a single alarmSchedule. This helps synchronize
 * between the ldenSchedules and the alarmSchedules.
 */
export function useAlarmScheduleField() {
    const { control, setValue } = useFormContext();
    const alarmSchedules = useWatch({
        name: 'notificationConfig.alarmSchedules',
        control,
    }) as Array<AlarmSchedule>;

    const { formController } = useSharedFormContext();
    const { fields } = formController.formSchema;
    const notificationConfig = fields.getByName('notificationConfig');

    const settings = useMemo(() => {
        const lmaxAlarm = notificationConfig.alarms.find(
            (alarm) => alarm.name === 'maxAlarms'
        ) as MaxAlarm;
        const leqAlarm = notificationConfig.alarms.find(
            (alarm) => alarm.name === 'leqAlarms'
        ) as LeqAlarm;

        if (!lmaxAlarm || !leqAlarm) {
            throw new Error('Could not find required lmaxFields or leqFields in the form schema.');
        }

        return {
            leqTitle: leqAlarm.title,
            lmaxAlarmLevel: lmaxAlarm.fields.find((field) => field.name === 'alarmLevel')!,
            leqAlarmLevel: leqAlarm.fields.find((field) => field.name === 'alarmLevel')!,
            leqInterval: leqAlarm.fields.find((field) => field.name === 'interval')!,
        };
    }, [notificationConfig]);

    const getAlarmSchedule = useCallback(
        (ldenSchedule: ModalFormData) =>
            findCorrespondingAlarmSchedule(ldenSchedule, alarmSchedules ?? []),
        [alarmSchedules]
    );

    const handleDelete = useCallback(
        (ldenSchedule: ModalFormData) => {
            const updatedAlarmSchedules =
                alarmSchedules?.filter(
                    (schedule: AlarmSchedule) =>
                        !isTimeWindowEqual(ldenSchedule, schedule as TimeWindow)
                ) ?? [];

            setValue('notificationConfig.alarmSchedules', updatedAlarmSchedules);
        },
        [alarmSchedules, setValue]
    );

    const handleSave = useCallback(
        (oldSettings: ModalFormData, newSettings: AlarmSchedule) => {
            const index = alarmSchedules.findIndex((schedule) =>
                isTimeWindowEqual(oldSettings, schedule as TimeWindowOnly)
            );
            if (index >= 0) {
                alarmSchedules[index] = newSettings;
            } else {
                alarmSchedules.push(newSettings);
            }
            // Update the form value.
            setValue('notificationConfig.alarmSchedules', alarmSchedules);
        },
        [alarmSchedules, setValue]
    );

    const handleSaveModalForm = useCallback(
        (ldenSchedule: ModalFormData, formData: ModalFormData) => {
            const { name, startDay, startTime, endDay, endTime, lmaxLevel, leqLevel, leqInterval } =
                formData;
            handleSave(ldenSchedule, {
                title: name,
                startDay,
                endDay,
                startTime,
                endTime,
                maxAlarms: lmaxLevel
                    ? [
                          {
                              title: `${name} Lmax`,
                              key: 'lden_lmax_1',
                              alarmLevel: lmaxLevel,
                              smsTemplate: '',
                          },
                      ]
                    : [],
                leqAlarms: leqLevel
                    ? [
                          {
                              title: `${name} Leq`,
                              key: 'lden_leq_1',
                              alarmLevel: leqLevel,
                              interval: leqInterval,
                          },
                      ]
                    : [],
                concentrationAlarms: [],
            });
        },
        [handleSave]
    );

    return {
        getAlarmSchedule,
        handleDelete,
        settings,
        handleSaveModalForm,
    };
}
